import { closest } from '@jotforminc/utils';
import { parseJSON } from '@jotforminc/form-common';

import {
  addClass, removeClass, hasClass, makeHiddenSubmissionKey, createInput
} from './utils';
import { ERROR_TYPES, TOOLTIP_TYPES } from './constants';
import {
  getUploaderTemplate, getUploadedFileTemplate, getUploaderSelectors, onUploadComplete, onUploadAdd
} from './uploader';

import ShoppingBag from './ShoppingBag';
import ProductPages from './ProductPages';
import Donation from './Donation';

export default class Patch {
  constructor(params) {
    this.params = params;
    this.formID = params.formID;
    this.storageEngine = params.storageEngine;
    this.continueLater = params.continueLater;
    this.showTooltip = params.showTooltip;
    this.patchSubmit = this.patchSubmit.bind(this);
    this.postFormData = this.postFormData.bind(this);
    this.patchErrored = this.patchErrored.bind(this);
    this.patchCorrected = this.patchCorrected.bind(this);
    this.patchDateTime = this.patchDateTime.bind(this);
    this.patchShowHideFields = this.patchShowHideFields.bind(this);
    this.patchRequireField = this.patchRequireField.bind(this);
    this.patchEnableDisableField = this.patchEnableDisableField.bind(this);
    this.patchCreditCardAddressVisibilityChanged = this.patchCreditCardAddressVisibilityChanged.bind(this);
    this.patchFileUploaderTemplates = this.patchFileUploaderTemplates.bind(this);
    this.patchIsVisible = this.patchIsVisible.bind(this);
    this.patchHandleSavedForm = this.patchHandleSavedForm.bind(this);
    this.patchHandlePaymentSubProducts = this.patchHandlePaymentSubProducts.bind(this);
    this.patchSetSubmissionID = this.patchSetSubmissionID.bind(this);
    this.patchInitShoppingBag = this.patchInitShoppingBag.bind(this);
    this.patchInitProductPages = this.patchInitProductPages.bind(this);
    this.patchInitDonation = this.patchInitDonation.bind(this);
    this.patchHandleSingleChoiceWithMultiTypeColumns = this.patchHandleSingleChoiceWithMultiTypeColumns.bind(this);
    this.patchHandleSingleChoiceWithMultiTypeColumnsDesktop = this.patchHandleSingleChoiceWithMultiTypeColumnsDesktop.bind(this);
    this.patchHandleSingleChoiceWithMultiTypeColumnsMobile = this.patchHandleSingleChoiceWithMultiTypeColumnsMobile.bind(this);
    this.patchSubmit();
    this.patchErrored();
    this.patchCorrected();
    this.patchDateTime();
    this.patchShowHideFields();
    this.patchRequireField();
    this.patchEnableDisableField();
    this.patchCreditCardAddressVisibilityChanged();
    this.patchFileUploaderTemplates();
    this.patchIsVisible();
    this.patchHandleSavedForm();
    this.patchSetSubmissionID();
    this.patchHandlePaymentSubProducts();
    this.patchInitShoppingBag();
    this.patchInitProductPages();
    this.patchInitDonation();
    global.JotForm.isFullScreen = () => hasClass(document.body, 'isFullScreen');
    global.JotForm.handleSingleChoiceWithMultiTypeColumnsForCardForms = this.patchHandleSingleChoiceWithMultiTypeColumns;
    global.JotForm.getContainer = element => {
      return global.JotForm.isFullScreen() ? document.getElementById('jfQuestion-proxy')
        : (closest(element, '.jfCard') || element.querySelector('.jfCard'));
    };

    // ********************************************

    this.checkFieldFilledClass = this.checkFieldFilledClass.bind(this);
  }

  patchSubmit() {
    document.querySelector('.jotform-form').addEventListener('submit', e => {
      // Continue with the normal form submit
      // var form = e.target;
      // var url = form.action;
      // var data = this.getFormData(form);
      // this.postFormData(url, data, (errStatus) => {
      //   if (errStatus) {
      //     console.log('error on submit');
      //   } else {
      //     console.log('form submitted');
      //     // switch to thankyou mode
      //   }
      // });

      if ((this.params.captchaMode === 'recaptcha_invisible' || this.params.captchaMode === 'invisibleFromAdvancedSettings')
        && global.grecaptcha && !global.captchaFilled && global.JotForm.validateAll(e.target)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        global.grecaptcha.execute();
        global.JotForm.enableButtons();
      } else if (this.params.onSubmit) {
        this.params.onSubmit(e);
      }
    });
  }

  postFormData(url, data, clb) {
    const http = new global.XMLHttpRequest();
    http.open('POST', url, true);

    // Send the proper header information along with the request
    http.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');

    http.onreadystatechange = () => {
      if (http.readyState === 4) {
        if (http.status === 200) {
          return clb();
        }
        return clb(http.status);
      }
      http.send(data);
    };
  }

  getFormData(form) {
    const setOrPush = (target, val) => {
      var result = val;
      if (target) {
        result = [target];
        result.push(val);
      }
      return result;
    };

    const getFormResults = formElement => {
      var formElements = formElement.elements;
      var formParams = {};
      var i = 0;
      var elem = null;
      for (i = 0; i < formElements.length; i += 1) {
        elem = formElements[i];
        switch (elem.type) {
          case 'submit':
            break;
          case 'radio':
            if (elem.checked) {
              formParams[elem.name] = elem.value;
            }
            break;
          case 'checkbox':
            if (elem.checked) {
              formParams[elem.name] = setOrPush(formParams[elem.name], elem.value);
            }
            break;
          default:
            formParams[elem.name] = setOrPush(formParams[elem.name], elem.value);
        }
      }
      return formParams;
    };

    return getFormResults(form);
  }

  // Development only for now.
  patchHandleSavedForm() {
    if (global.JotForm.handleSavedForm) { // eslint-disable-line
      const originalHandleSavedForm = global.JotForm.handleSavedForm;
      global.JotForm.handleSavedForm = (...params) => {
        const hiddenSubmissionKey = makeHiddenSubmissionKey(this.formID);
        const { sessionID, submissionToken, submissionID } = global.JotForm;
        // Lets check if client has a hidden submission id on local storage, or populate from url.
        let previousSessionObject = (this.continueLater.useStorage && this.storageEngine.isAvailable()) ? this.storageEngine.get(hiddenSubmissionKey) : null;
        if (previousSessionObject && (!sessionID || !submissionToken || !submissionID)) {
          previousSessionObject = parseJSON(previousSessionObject);
          if (!previousSessionObject) { // If data is not valid, erase it.
            this.storageEngine.delete(hiddenSubmissionKey);
          }
          global.JotForm.sessionID = previousSessionObject.sessionID;
          global.JotForm.submissionToken = previousSessionObject.submissionToken;
          global.JotForm.submissionID = previousSessionObject.submissionID;
        }

        const form = global.document.forms[0];
        if (global.JotForm.submissionToken) {
          const submissionTokenInput = createInput({
            type: 'hidden', name: 'submission_token', id: 'submission_token', value: global.JotForm.submissionToken
          });
          form.appendChild(submissionTokenInput);
        }
        if (global.JotForm.submissionID) {
          const submissionIDInput = createInput({
            type: 'hidden', name: 'submission_id', id: 'submission_id', value: global.JotForm.submissionID
          });
          form.appendChild(submissionIDInput);
        }
        if (global.JotForm.sessionID) {
          const sessionInput = createInput({
            type: 'hidden', name: 'session_id', id: 'session', value: global.JotForm.sessionID
          });
          form.appendChild(sessionInput);
          originalHandleSavedForm.call(global.JotForm, ...params);
        }
      };
    }
  }

  patchSetSubmissionID() {
    if (global.JotForm.setSubmissionID) {
      const originalSetSubmissionID = global.JotForm.setSubmissionID;
      global.JotForm.setSubmissionID = (...params) => {
        originalSetSubmissionID.call(global.JotForm, ...params);
      };
    }
  }

  patchErrored() {
    if (global.JotForm.errored) {
      global.JotForm.errored = (_input, _message, dontShowMessage, errorType) => {
        const input = _input;
        const message = _message.replace(/<br>/g, '');
        const isFullScreen = global.JotForm.isFullScreen();

        if (dontShowMessage) return false;
        if (input.errored) {
          return false;
        }

        // other choice add hasOtherError for style
        const otherCheckboxInput = input.parentElement ? input.parentElement.querySelector('.jfCheckbox-otherInputField') : null;
        const otherRadioInput = input.parentElement ? input.parentElement.querySelector('.jfRadio-otherInputField') : null;
        if (hasClass(input, 'jfCheckbox-otherInput') && otherCheckboxInput !== null && otherCheckboxInput.value === '' && input.checked) {
          const otherLine = closest(input, '.jfField', '.jfCard');
          addClass(otherLine, 'hasOtherError');
        } else if (hasClass(input, 'jfRadio-otherInput') && otherRadioInput !== null && otherRadioInput.value === '' && input.checked) {
          const otherLine = closest(input, '.jfField', '.jfCard');
          addClass(otherLine, 'hasOtherError');
        }

        if (errorType === ERROR_TYPES.TOOLTIP) {
          this.showTooltip(input, { message, type: TOOLTIP_TYPES.ERROR });
        }

        if (input.id && input.id.indexOf('disabled') > 0) {
          // ignore matrix hidden fields
          return true;
        }

        input.errored = true;
        input.errorMessage = _message;
        addClass(input, 'form-validation-error');

        // if (hasClass(container, 'form-line-error')) return;
        const card = global.JotForm.getContainer(input);

        if (!isFullScreen) {
          var line = closest(input, '.form-line');
          addClass(line, 'form-line-error');
        } else {
          addClass(card, 'hasError');
        }

        var insertEl = isFullScreen ? card : card.querySelector('.jfCard-actionsNotification');
        var mobileErrorWrapper = card.querySelector('.jsMobileErrorWrapper');
        const addElementIfNotExists = (_card, errorClass, errorWrapper, addBefore) => {
          if (!_card.querySelector(`.${errorClass}`)) {
            const errorElement = new global.Element('div', {
              className: addBefore ? `${errorClass} jfCard-error` : errorClass,
              role: 'alert'
            });
            errorElement.textContent = message;
            if (errorWrapper) {
              if (addBefore) {
                errorWrapper.prepend(errorElement);
              } else {
                errorWrapper.appendChild(errorElement);
              }
            }
          }

          if (_card.querySelector(`.${errorClass}`)) {
            var existingErrorElement = _card.querySelector(`.${errorClass}`);
            existingErrorElement.textContent = message;
          }
        };

        if (errorType !== ERROR_TYPES.TOOLTIP) {
          addElementIfNotExists(card, 'form-error-message', insertEl, isFullScreen);
          addElementIfNotExists(card, 'form-mobile-error-message', mobileErrorWrapper, false);
        }

        if (this.params.onFieldErrored) {
          this.params.onFieldErrored(input);
        }

        return false;
      };
    }
  }

  patchCorrected() {
    if (global.JotForm.corrected) {
      global.JotForm.corrected = _input => {
        const input = _input;
        input.errored = false;

        const line = closest(input, '.form-line');
        const container = global.JotForm.getContainer(input);
        if (!container) {
          return true;
        }

        removeClass(container, 'hasError');

        if (input.id && input.id.indexOf('disabled') > 0) {
          // ignore matrix hidden fields
          return true;
        }

        // other choice remove hasOtherError for style
        const otherCheckboxInput = input.parentElement ? input.parentElement.querySelector('.jfCheckbox-otherInputField') : null;
        const otherRadioInput = input.parentElement ? input.parentElement.querySelector('.jfRadio-otherInputField') : null;
        if (hasClass(input, 'jfCheckbox-otherInput') && otherCheckboxInput !== null && (!input.checked || (otherCheckboxInput.value !== '' && input.checked))) {
          const otherLine = closest(input, '.jfField', '.jfCard');
          removeClass(otherLine, 'hasOtherError');
        } else if (hasClass(input, 'jfRadio-otherInput') && otherRadioInput !== null && (!input.checked || (otherRadioInput.value !== '' && input.checked))) {
          const otherLine = closest(input, '.jfField', '.jfCard');
          removeClass(otherLine, 'hasOtherError');
        }

        removeClass(line, 'form-line-error');
        removeClass(input, 'form-validation-error');
        const erroredChildren = container.querySelectorAll('.form-validation-error');

        // Remove child errors on matrix corrected
        // fixes #1383205
        if (line !== null && typeof line !== 'undefined'
          && typeof line.getAttribute === 'function'
          && line.getAttribute('data-type') === 'control_matrix') {
          Array.prototype.forEach.call(erroredChildren, e => {
            removeClass(e, 'form-validation-error');
          });
        }

        if (line && line.querySelectorAll('.form-validation-error').length === 0) {
          removeClass(line, 'form-line-error');
        }

        const errors = container.querySelectorAll('.form-error-message, .form-mobile-error-message');
        if (errors && erroredChildren.length === 0) {
          Array.prototype.forEach.call(errors, error => {
            error.parentElement.removeChild(error);
          });
        }

        if (this.params.onFieldCorrected) {
          this.params.onFieldCorrected(input);
        }

        return true;
      };
    }
  }

  patchDateTime() {
    if (global.Calendar) {
      const self = this;
      this.originalCalendarSetup = global.Calendar.setup;
      const { checkFieldFilledClass } = this;
      global.Calendar.setup = options => {
        const originalSelectHandler = options.selectHandler;

        function selectHandler(...params) {
          const calendarObject = params[0];
          originalSelectHandler.call(this, ...params);
          checkFieldFilledClass(calendarObject.dateField);
          const cardContainer = closest(params[0].dateField, '.jfCard');
          self.params.onCalendarSelected(cardContainer, params[1]);
        }

        const calendar = this.originalCalendarSetup({
          dateField: options.dateField,
          parentElement: options.parentElement,
          triggerElement: options.triggerElement,
          startOnMonday: options.startOnMonday,
          limits: options.limits,
          selectHandler: selectHandler
        });

        self.params.onCalendarInit(calendar, options.parentElement);

        return calendar;
      };
    }
  }

  checkFieldFilledClass(input) {
    if (input.validateInput) input.validateInput();
    // const container = closest(input, '.jfCard-question');
    // forEach(container.querySelectorAll('.jfField'), (field) => {
    //   const inputField = field.querySelector('input');
    //   if (inputField) {
    //     if (inputField.value) {
    //       addClass(field, 'isFilled');
    //     } else {
    //       removeClass(field, 'isFilled');
    //     }
    //   }
    // });
  }

  patchShowHideFields() {
    const { showField, hideField } = global.JotForm;

    global.JotForm.showOrHideMultipleFields = (...params) => {
      const [shouldBeVisible, fields, ...additionalParams] = params;
      const fieldIds = [];
      const failedFieldIds = [];
      const callable = shouldBeVisible ? showField : hideField;

      fields.forEach((field) => {
        if (callable.call(global.JotForm, field, ...additionalParams)) {
          fieldIds.push(field);
        } else {
          failedFieldIds.push(field);
        }
      })

      if (fieldIds.length) {
        this.params.onFieldVisibilityChanged.call(null, true, shouldBeVisible, fieldIds);
      }

      if (failedFieldIds.length) {
        this.params.onFieldVisibilityChanged.call(null, false, shouldBeVisible, failedFieldIds);
      }
    };

    global.JotForm.showField = (...params) => {
      const success = showField.call(global.JotForm, ...params);
      this.params.onFieldVisibilityChanged.call(null, success, true, ...params);
    };

    global.JotForm.hideField = (...params) => {
      const success = hideField.call(global.JotForm, ...params);
      this.params.onFieldVisibilityChanged.call(null, success, false, ...params);
    };
  }

  patchRequireField() {
    const originalRequire = global.JotForm.requireField;

    global.JotForm.requireField = (...params) => {
      const [id, isRequired] = params;
      originalRequire.call(global.JotForm, ...params);
      this.params.onRequireField.call(null, id, isRequired);
    };
  }

  patchEnableDisableField() {
    const originalFunc = global.JotForm.enableDisableField;
    global.JotForm.enableDisableField = (...params) => {
      const [id, enable] = params;
      originalFunc.call(global.JotForm, ...params);
      this.params.onEnableDisableField.call(null, id, enable);
    };
  }

  patchFileUploaderTemplates() {
    if (global.qq) {
      global.qq.getUploaderTemplate = (...params) => { return getUploaderTemplate(...params); };
      global.qq.getUploadedFileTemplate = (...params) => { return getUploadedFileTemplate(...params); };
      global.qq.getUploaderSelectors = (...params) => { return getUploaderSelectors(...params); };
      global.qq.onUploadComplete = (...params) => { this.params.onFileUploadChanged(params[4]); return onUploadComplete(...params); };
      global.qq.onDelete = (...params) => { this.params.onFileUploadChanged(params[0]); };
      global.qq.onUploadAdd = (...params) => { return onUploadAdd(...params); };
    }
  }

  patchCreditCardAddressVisibilityChanged() {
    // const originalFunc = global.JotForm.setCreditCardVisibility;
    global.JotForm.setCreditCardVisibility = isVisible => {
      // originalFunc.call(global.JotForm, isVisible);
      this.params.onCreditCardAddressVisibilityChanged.call(null, isVisible);
    };
  }

  // jotform validates only visible fields
  // datetime should be validated by hidden inputs
  // so override isVisible and return true if it has cf-validate-hidden class
  patchIsVisible() {
    const threatLikeVisibleClass = 'cf-valid-hidden';
    const originalFunc = global.JotForm.isVisible;
    global.JotForm.isVisible = (...params) => {
      const [element] = params;
      if (hasClass(element, threatLikeVisibleClass)) originalFunc.call(global.JotForm, { ...params, element: element.parentElement });
      return originalFunc.call(global.JotForm, ...params);
    };
  }

  patchHandlePaymentSubProducts() {
    global.JotForm.handlePaymentSubProducts = () => {
      const paymentWithSubProduct = document.querySelectorAll('.form-product-has-subproducts');
      paymentWithSubProduct.forEach(product => {
        const productWrapper = closest(product, '.jfProduct-list', '.form-line') ? closest(product, '.jfProduct-list', '.form-line') : closest(product, '.js-product-detail', '.form-line');
        product.addEventListener('click', () => {
          const subProductsQuantity = productWrapper.querySelectorAll('.form-subproduct-quantity');
          if (!product.checked) {
            subProductsQuantity.forEach(sp => {
              const subproduct = sp;
              subproduct.value = 0;
              if (sp.tagName === 'SELECT') {
                const changeEvent = document.createEvent('HTMLEvents');
                changeEvent.initEvent('change', false, true);
                sp.dispatchEvent(changeEvent);
              }
            });
            // resume calculation
            setTimeout(() => {
              global.JotForm.totalCounter(global.JotForm.prices);
            }, 300);
          }
        });
      });
    };
  }

  patchInitShoppingBag() {
    global.JotForm.initShoppingBag = (id, formProducts, currency, enableImagePreview) => {
      const productPages = new ProductPages(id);
      global.JotForm.productPages = productPages;
      productPages.initProductPages();

      global.JotForm.shoppingBag = new ShoppingBag(id, formProducts, currency, productPages, enableImagePreview);
      global.JotForm.shoppingBag.initShoppingBag();
    };
  }

  patchInitProductPages() {
    global.JotForm.initProductPages = () => {};
  }

  patchInitDonation() {
    // const originalFunc = global.JotForm.initDonation;
    global.JotForm.initDonation = (id, currency) => {
      const donation = new Donation(id, currency);
      donation.initDonation();
    };
  }

  patchHandleSingleChoiceWithMultiTypeColumns() {
    this.patchHandleSingleChoiceWithMultiTypeColumnsMobile();
    this.patchHandleSingleChoiceWithMultiTypeColumnsDesktop();
  }

  patchHandleSingleChoiceWithMultiTypeColumnsMobile() {
    const listItem = Array.prototype.slice.call(document.querySelectorAll('.jfMatrixChoice-row'));
    if (!listItem) return;
    listItem.forEach(e => {
      const inputCellChild = e.querySelector('.jfMatrixChoice-inputCell');
      if (inputCellChild) {
        inputCellChild.addEventListener('click', () => {
          const formRadioElements = e.getElementsByClassName('form-radio');
          const selectedItem = formRadioElements.length ? formRadioElements[0] : null;
          if (selectedItem && selectedItem.name && e.parentElement) {
            const parent = e.parentElement; // Should get the jfMatrixChoice-table
            const selectedElementName = selectedItem.name;
            const parentCells = parent.getElementsByClassName('jfMatrixChoice-inputCell');
            const parentCellsLength = parentCells.length;
            for (let i = 0; i < parentCellsLength; i++) {
              const matrixCellsItem = parentCells[i];
              const radioCellItem = matrixCellsItem.children.length ? matrixCellsItem.children[0] : null;
              if (
                radioCellItem
                && selectedItem.type === 'radio'
                && radioCellItem.children
                && radioCellItem.children[0]
                && radioCellItem.children[0].name !== selectedElementName
              ) {
                const notSelectedItem = radioCellItem.children[0];
                if (notSelectedItem.type === 'radio') notSelectedItem.setValue(null);
              }
            }
          }
        });
      }
    });
  }

  patchHandleSingleChoiceWithMultiTypeColumnsDesktop() {
    const listItem = Array.prototype.slice.call(document.querySelectorAll('.jfMatrixTable-cell[role="gridcell"]'));
    if (!listItem) return;
    listItem.forEach(e => {
      const firstChildOfE = e.children.length ? e.children[0] : null;
      if (firstChildOfE) {
        firstChildOfE.addEventListener('click', () => {
          const formRadioElements = e.getElementsByClassName('form-radio');
          const selectedItem = formRadioElements.length ? formRadioElements[0] : null;
          if (selectedItem && selectedItem.name && e.parentElement) {
            const parent = e.parentElement; // Should get the row element
            const selectedElementName = selectedItem.name;
            const parentCells = parent.getElementsByClassName('jfMatrixTable-cell');
            const parentCellsLength = parentCells.length;
            for (let i = 0; i < parentCellsLength; i++) {
              const matrixCellsItem = parentCells[i];
              const radioCellItem = matrixCellsItem.children.length ? matrixCellsItem.children[0] : null;
              if (
                radioCellItem
                && selectedItem.type === 'radio'
                && radioCellItem.children
                && radioCellItem.children[0]
                && radioCellItem.children[0].name !== selectedElementName
              ) {
                const notSelectedItem = radioCellItem.children[0];
                if (notSelectedItem.type === 'radio') notSelectedItem.setValue(null);
              }
            }
          }
        });
      }
    });
  }
}
